// Daily-View control row: focus on the top-priority orders (largest / critical)
// and slice by fill-ticket coverage. Integrated into the Sankey HUD; emits the
// whole filter state up on change.

import type { CoverageMode, DailyViewFilterState } from './types';
import styles from './PriorityCoverageControls.module.css';

const COVERAGE_OPTS: { key: CoverageMode; label: string; title: string }[] = [
  { key: 'all', label: 'All', title: 'Show every order line' },
  {
    key: 'covered',
    label: 'Covered',
    title: 'Only lines already covered by an open fill ticket',
  },
  {
    key: 'missing',
    label: 'Missing',
    title: 'Only lines with no open fill ticket — the fill tickets still to create',
  },
];

const TOP_N_OPTS = [0, 10, 25, 50];

export function PriorityCoverageControls({
  state,
  onChange,
  covered,
  missing,
  hasTickets,
}: {
  state: DailyViewFilterState;
  onChange: (next: DailyViewFilterState) => void;
  covered: number;
  missing: number;
  hasTickets: boolean;
}) {
  const dirty =
    state.coverage !== 'all' || state.topN > 0 || state.criticalOnly;

  return (
    <div className={styles.row}>
      <div className={styles.group}>
        <span className={styles.lbl}>Fill tickets</span>
        <div className={styles.seg} role="group" aria-label="Fill-ticket coverage">
          {COVERAGE_OPTS.map((o) => {
            const active = state.coverage === o.key;
            const cls = [
              styles.segBtn,
              active ? styles.segActive : '',
              active && o.key === 'missing' ? styles.segMissing : '',
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <button
                key={o.key}
                type="button"
                title={o.title}
                className={cls}
                disabled={!hasTickets && o.key !== 'all'}
                onClick={() => onChange({ ...state, coverage: o.key })}
              >
                {o.label}
              </button>
            );
          })}
        </div>
        {hasTickets ? (
          <span className={styles.counts}>
            <span className={styles.bad}>{missing}</span> missing&nbsp;·&nbsp;
            <span className={styles.good}>{covered}</span> covered
          </span>
        ) : (
          <span className={styles.note}>no ticket data</span>
        )}
      </div>

      <div className={styles.group}>
        <span className={styles.lbl}>Priority</span>
        <label className={styles.chk} title="Restrict to orders that depend on an incoming PO">
          <input
            type="checkbox"
            checked={state.criticalOnly}
            onChange={(e) => onChange({ ...state, criticalOnly: e.target.checked })}
          />
          Critical&nbsp;POs only
        </label>
        <label
          className={styles.inline}
          title="Keep only the N highest-priority orders (by the model's order score when the run carries one; critical first, then by size otherwise)"
        >
          top
          <select
            value={String(state.topN)}
            onChange={(e) => onChange({ ...state, topN: Number(e.target.value) })}
          >
            {TOP_N_OPTS.map((n) => (
              <option key={n} value={n}>
                {n === 0 ? 'all' : n}
              </option>
            ))}
          </select>
          by priority
        </label>
        {dirty && (
          <button
            type="button"
            className={styles.reset}
            onClick={() => onChange({ coverage: 'all', topN: 0, criticalOnly: false })}
          >
            reset
          </button>
        )}
      </div>
    </div>
  );
}
